/**
 * DemoSimulatorPanel — Panel de QA para simular el flujo de reunión.
 * Solo se monta cuando VITE_DEMO_MODE=true. Eliminar esta carpeta para producción.
 */

import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOUs } from '@/hooks/useOUs';
import type { OUNode } from '@/data/mockOUs';
import { loadSlots, saveSlots, loadActiveSlotIndex, saveActiveSlotIndex } from './demoStorage';
import type { DemoSlot, SimulateResult } from './demoTypes';
import { useUserContext } from '@/contexts/UserContext';
import { pushDebugEntry } from '@/debug/debugStore';
import './DemoSimulatorPanel.css';

const MEETING_API = import.meta.env.VITE_API_MEETING_URL as string;

// ─── Slot Editor ──────────────────────────────────────────────────────────────

interface SlotEditorProps {
  slot: DemoSlot;
  index: number;
  isActive: boolean;
  ous: OUNode[];
  onActivate: () => void;
  onChange: (updated: DemoSlot) => void;
}

function SlotEditor({ slot, index, isActive, ous, onActivate, onChange }: SlotEditorProps) {
  return (
    <div className={`demo-slot${isActive ? ' demo-slot--active' : ''}`}>
      <div className="demo-slot-header">
        <button
          className="demo-slot-avatar"
          onClick={onActivate}
          title="Ver como este usuario"
          type="button"
        >
          {slot.nombre.trim() ? slot.nombre.trim().split(' ').slice(0, 2).map(p => p[0]?.toUpperCase()).join('') : `U${index + 1}`}
        </button>
        <div className="demo-slot-fields">
          <input
            className="demo-slot-input"
            placeholder={`Nombre usuario ${index + 1}`}
            value={slot.nombre}
            onChange={e => onChange({ ...slot, nombre: e.target.value })}
          />
          <input
            className="demo-slot-input demo-slot-email"
            placeholder="correo@sigma.demo"
            value={slot.correo}
            onChange={e => onChange({ ...slot, correo: e.target.value })}
          />
        </div>
      </div>
      <div className="demo-slot-row">
        <select
          className="demo-slot-select"
          value={slot.ouId}
          onChange={e => onChange({ ...slot, ouId: e.target.value })}
        >
          <option value="">— OU —</option>
          {ous.map(ou => (
            <option key={ou.key} value={ou.key}>{ou.label}</option>
          ))}
        </select>
        <select
          className="demo-slot-select demo-slot-tenant"
          value={slot.tenant}
          onChange={e => onChange({ ...slot, tenant: e.target.value as 'LATAM' | 'Europa' })}
        >
          <option value="LATAM">LATAM</option>
          <option value="Europa">Europa</option>
        </select>
      </div>
      <div className="demo-slot-id" title="AAD Object ID simulado">{slot.userId.slice(0, 8)}…</div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function DemoSimulatorPanel() {
  const qc = useQueryClient();
  const { demoUser, setDemoUser } = useUserContext();
  const { data: ous = [] } = useOUs();
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState<DemoSlot[]>(() => loadSlots());
  const [activeSlotIndex, setActiveSlotIndex] = useState(() => loadActiveSlotIndex());

  // Sync the initial active slot to UserContext so hooks reading demoUser.userId are correct on mount
  useEffect(() => {
    const slot = loadSlots()[loadActiveSlotIndex()];
    if (slot) {
      setDemoUser({
        ...demoUser,
        userId: slot.userId,
        nombre: slot.nombre || demoUser.nombre,
        correo: slot.correo || demoUser.correo,
        ou: slot.ouId || demoUser.ou,
        tenant: slot.tenant,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Simulation form state
  const [selectedParticipants, setSelectedParticipants] = useState<boolean[]>([true, true, false, false]);
  const [organizerIndex, setOrganizerIndex] = useState(0);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [meetingId, setMeetingId] = useState('');

  const [simResult, setSimResult] = useState<SimulateResult | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);

  const updateSlot = useCallback((index: number, updated: DemoSlot) => {
    setSlots(prev => {
      const next = [...prev];
      next[index] = updated;
      saveSlots(next);
      return next;
    });
  }, []);

  const activateSlot = useCallback((index: number) => {
    setActiveSlotIndex(index);
    saveActiveSlotIndex(index);
    const slot = slots[index];
    if (slot) {
      // Update UserContext so all hooks reading demoUser.userId reactively get the new user
      setDemoUser({
        ...demoUser,
        userId: slot.userId,
        nombre: slot.nombre || demoUser.nombre,
        correo: slot.correo || demoUser.correo,
        ou: slot.ouId || demoUser.ou,
        tenant: slot.tenant,
      });
    }
    void qc.invalidateQueries({ queryKey: ['pendingInstances'] });
    void qc.invalidateQueries({ queryKey: ['dashboardStats'] });
    void qc.invalidateQueries({ queryKey: ['currentScore'] });
  }, [qc, slots, demoUser, setDemoUser]);

  const toggleParticipant = (index: number) => {
    setSelectedParticipants(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const participatingSlots = slots.filter((_, i) => selectedParticipants[i]);

  async function handleSimulate() {
    if (participatingSlots.length < 2) {
      setSimError('Selecciona al menos 2 participantes.');
      return;
    }

    const organizerSlot = slots[organizerIndex];
    if (!selectedParticipants[organizerIndex]) {
      setSimError('El organizador debe ser uno de los participantes seleccionados.');
      return;
    }

    const missingOu = participatingSlots.find(s => !s.ouId);
    if (missingOu) {
      setSimError(`El usuario "${missingOu.nombre}" no tiene OU asignada.`);
      return;
    }

    const tenantId = slots[activeSlotIndex]?.tenant ?? 'LATAM';

    setSimLoading(true);
    setSimError(null);
    setSimResult(null);

    const reqBody = {
      organizerUserId: organizerSlot.userId,
      organizerOuId: organizerSlot.ouId,
      participants: participatingSlots.map(s => ({ userId: s.userId, ouId: s.ouId, displayName: s.nombre || undefined })),
      durationMinutes,
      tenantId,
      meetingId: meetingId.trim() || undefined,
    };
    const debugUrl = `${MEETING_API}/api/meetings/simulate`;
    const startMs = Date.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15_000);

      let res: Response;
      try {
        res = await fetch(debugUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify(reqBody),
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (res.status === 404) {
        pushDebugEntry({ id: crypto.randomUUID(), timestamp: startMs, method: 'POST', service: 'meeting', path: '/api/meetings/simulate', url: debugUrl, status: 404, durationMs: Date.now() - startMs, requestBody: reqBody, responseBody: null, isError: true });
        throw new Error(
          'Endpoint no encontrado (404). El servicio MeetingService necesita ser redesplegado con el código actualizado. ' +
          'En local: asegúrate de que MeetingService esté corriendo en el puerto 5252.'
        );
      }

      if (!res.ok) {
        const text = await res.text();
        pushDebugEntry({ id: crypto.randomUUID(), timestamp: startMs, method: 'POST', service: 'meeting', path: '/api/meetings/simulate', url: debugUrl, status: res.status, durationMs: Date.now() - startMs, requestBody: reqBody, responseBody: text, isError: true });
        throw new Error(`HTTP ${res.status}: ${text}`);
      }

      const data = await res.json() as SimulateResult;
      pushDebugEntry({ id: crypto.randomUUID(), timestamp: startMs, method: 'POST', service: 'meeting', path: '/api/meetings/simulate', url: debugUrl, status: res.status, durationMs: Date.now() - startMs, requestBody: reqBody, responseBody: data, isError: false });
      setSimResult(data);

      if (data.isEligible) {
        // Esperar ~4s a que Service Bus entregue el evento y SurveyService procese
        setTimeout(() => {
          void qc.invalidateQueries({ queryKey: ['pendingInstances'] });
          void qc.invalidateQueries({ queryKey: ['dashboardStats'] });
          void qc.invalidateQueries({ queryKey: ['currentScore'] });
        }, 4000);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        pushDebugEntry({ id: crypto.randomUUID(), timestamp: startMs, method: 'POST', service: 'meeting', path: '/api/meetings/simulate', url: debugUrl, status: null, durationMs: Date.now() - startMs, requestBody: reqBody, responseBody: null, isError: true });
        setSimError('Timeout (15s): el servicio MeetingService no respondió. Verifica que esté corriendo y redesplegado.');
      } else {
        setSimError(err instanceof Error ? err.message : 'Error desconocido');
      }
    } finally {
      setSimLoading(false);
    }
  }

  const activeSlot = slots[activeSlotIndex];

  return (
    <>
      {/* FAB */}
      <button
        className="demo-fab"
        onClick={() => setOpen(o => !o)}
        title="Demo Panel — QA"
        aria-label="Abrir Demo Panel"
      >
        <i className="fas fa-flask" aria-hidden="true" />
        <span className="demo-fab-badge">{activeSlot.nombre.split(' ')[0] || `U${activeSlotIndex + 1}`}</span>
      </button>

      {/* Panel drawer */}
      {open && (
        <div className="demo-overlay" onClick={() => setOpen(false)}>
          <div className="demo-panel" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="demo-panel-header">
              <span><i className="fas fa-flask" aria-hidden="true" /> Demo Panel · QA</span>
              <button className="demo-close" onClick={() => setOpen(false)} aria-label="Cerrar">
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>

            {/* Active user indicator */}
            <div className="demo-active-label">
              <i className="fas fa-eye" aria-hidden="true" />
              Viendo como: <strong>{activeSlot.nombre || `Usuario ${activeSlotIndex + 1}`}</strong>
              {activeSlot.ouId && <span className="demo-ou-badge">{activeSlot.ouId}</span>}
            </div>

            {/* ── Slots section ─────────────────────────── */}
            <div className="demo-section-label">
              <i className="fas fa-users" aria-hidden="true" /> Usuarios demo
            </div>
            <p className="demo-hint">
              Haz clic en el avatar para cambiar de perspectiva. Guarda los cambios automáticamente.
            </p>
            <div className="demo-slots-grid">
              {slots.map((slot, i) => (
                <SlotEditor
                  key={i}
                  slot={slot}
                  index={i}
                  isActive={i === activeSlotIndex}
                  ous={ous}
                  onActivate={() => activateSlot(i)}
                  onChange={updated => updateSlot(i, updated)}
                />
              ))}
            </div>

            {/* ── Simulation section ────────────────────── */}
            <div className="demo-section-label" style={{ marginTop: '16px' }}>
              <i className="fas fa-play-circle" aria-hidden="true" /> Simular reunión
            </div>
            <p className="demo-hint">
              Selecciona participantes, organizador y duración. Se ejecuta el flujo real (sin Graph, sin delay).
            </p>

            {/* Participant checkboxes */}
            <div className="demo-participants">
              {slots.map((slot, i) => (
                <label key={i} className="demo-participant-check">
                  <input
                    type="checkbox"
                    checked={selectedParticipants[i]}
                    onChange={() => toggleParticipant(i)}
                  />
                  <span>{slot.nombre || `Usuario ${i + 1}`}</span>
                  {slot.ouId && <span className="demo-ou-badge">{slot.ouId}</span>}
                </label>
              ))}
            </div>

            {/* Organizer + duration */}
            <div className="demo-sim-row">
              <div className="demo-sim-field">
                <label className="demo-label">Organizador</label>
                <select
                  className="demo-select"
                  value={organizerIndex}
                  onChange={e => setOrganizerIndex(Number(e.target.value))}
                >
                  {slots.map((slot, i) => (
                    <option key={i} value={i} disabled={!selectedParticipants[i]}>
                      {slot.nombre || `Usuario ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="demo-sim-field">
                <label className="demo-label">Duración (min)</label>
                <input
                  className="demo-input"
                  type="number"
                  min={1}
                  max={480}
                  value={durationMinutes}
                  onChange={e => setDurationMinutes(Number(e.target.value))}
                />
              </div>
            </div>

            {/* Meeting ID for personalized surveys */}
            <div className="demo-sim-field" style={{ marginTop: '8px' }}>
              <label className="demo-label">Meeting ID (opcional — para encuestas personalizadas)</label>
              <input
                className="demo-input"
                placeholder="ej. sim-reunion-ventas-2026"
                value={meetingId}
                onChange={e => setMeetingId(e.target.value)}
              />
            </div>

            {/* Error */}
            {simError && (
              <div className="demo-error">
                <i className="fas fa-exclamation-triangle" aria-hidden="true" /> {simError}
              </div>
            )}

            {/* Result */}
            {simResult && (
              <div className={`demo-result ${simResult.isEligible ? 'demo-result--ok' : 'demo-result--warn'}`}>
                {simResult.isEligible ? (
                  <>
                    <div><i className="fas fa-check-circle" aria-hidden="true" /> <strong>Reunión elegible</strong></div>
                    <div>ID: <code>{simResult.meetingId}</code></div>
                    <div>Participantes elegibles: <strong>{simResult.eligibleParticipants}</strong></div>
                    <div>Instancias esperadas: <strong>{simResult.expectedSurveyInstances}</strong></div>
                    <div className="demo-result-note">
                      <i className="fas fa-sync-alt" aria-hidden="true" /> Las encuestas aparecerán en ~2s…
                    </div>
                  </>
                ) : (
                  <>
                    <div><i className="fas fa-times-circle" aria-hidden="true" /> <strong>Reunión no elegible</strong></div>
                    <div>{simResult.ineligibleReason}</div>
                  </>
                )}
              </div>
            )}

            {/* Simulate button */}
            <button
              className="demo-btn-simulate"
              onClick={() => void handleSimulate()}
              disabled={simLoading}
            >
              {simLoading
                ? <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Simulando…</>
                : <><i className="fas fa-play" aria-hidden="true" /> Simular reunión</>
              }
            </button>

          </div>
        </div>
      )}
    </>
  );
}
